import { getLayout } from './utils/layout';
import _ from 'lodash';

import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef, CompLayout } from 'types/documentServices';
import type { Gap } from './resizeGapsTypes';

// When gap is low, it's hard (and even impossible for zero-height gap)
// for user to hit it with cursor, so we add some padding to the
// component for making it more user-friendly.
const PADDING_WHEN_GAP_STILL_VISIBLE = 5;

export const findGapUnderCursor = (editorAPI: EditorAPI): Gap | null => {
  const selectedComps = editorAPI.selection.getSelectedComponents();
  if (selectedComps.length !== 1) {
    return null;
  }
  const selectedComp = selectedComps[0];

  if (
    !isCursorWithinComponent(editorAPI, selectedComp) ||
    isHeaderFooterOrItsDescendant(editorAPI, selectedComp)
  ) {
    return null;
  }

  const getLayoutMemoized = _.memoize((compRef: CompRef) =>
    getLayout(editorAPI, compRef),
  );

  const container = getGapContainer(editorAPI, selectedComp);
  const children = getVisibleAndSelectableChildren(
    editorAPI,
    container,
    getLayoutMemoized,
  );

  if (!children.length) {
    return null;
  }

  let gapTop = getLayoutMemoized(container).y;
  let gapBottom =
    getLayoutMemoized(container).y + getLayoutMemoized(container).height;
  let componentBelow: CompRef | null = null;
  let componentAbove: CompRef | null = null;

  const { y: mouseY } = editorAPI.cursor.get();

  for (const child of children) {
    const childTop = getLayoutMemoized(child).y;
    const childBottom =
      getLayoutMemoized(child).y + getLayoutMemoized(child).height;

    if (
      mouseY >= childTop + PADDING_WHEN_GAP_STILL_VISIBLE &&
      mouseY <= childBottom - PADDING_WHEN_GAP_STILL_VISIBLE
    ) {
      // mouse is either inside component or in the horizontal gap,
      // but not close to edge of the comp
      return null;
    }

    if (
      mouseY > childBottom - PADDING_WHEN_GAP_STILL_VISIBLE &&
      childBottom > gapTop
    ) {
      gapTop = childBottom;
      componentAbove = child;
    }

    if (
      mouseY < childTop + PADDING_WHEN_GAP_STILL_VISIBLE &&
      childTop < gapBottom
    ) {
      gapBottom = childTop;
      componentBelow = child;
    }
  }

  const gap = {
    container,
    componentAbove,
    componentBelow,
    layout: {
      y: gapTop,
      x: getLayoutMemoized(container).x,
      width: getLayoutMemoized(container).width,
      height: gapBottom - gapTop,
    },
  };

  if (gap.layout.height < 0) {
    return null;
  }

  return gap;
};

function isHeaderFooterOrItsDescendant(editorAPI: EditorAPI, comp: CompRef) {
  return (
    editorAPI.sections.isHeaderOrFooter(comp) ||
    !!editorAPI.sections.getClosestHeaderFooter(comp)
  );
}

function isCursorWithinComponent(editorAPI: EditorAPI, comp: CompRef) {
  const { x, y } = editorAPI.cursor.get();
  const layout = getLayout(editorAPI, comp);

  return (
    x >= layout.x &&
    y >= layout.y &&
    x <= layout.x + layout.width &&
    y <= layout.y + layout.height
  );
}

function getGapContainer(
  editorAPI: EditorAPI,
  selectedComp: CompRef,
): CompRef | null {
  const selectedCompType = editorAPI.components.getType(selectedComp);
  const validContainerTypes = [
    'mobile.core.components.Container',
    'wysiwyg.viewer.components.Column',
    'wysiwyg.viewer.components.ClassicSection',
    'wysiwyg.viewer.components.StripColumnsContainer',
  ];

  if (!validContainerTypes.includes(selectedCompType)) {
    return null;
  }

  if (selectedCompType === 'wysiwyg.viewer.components.StripColumnsContainer') {
    // When a strip has only one column, the column
    // itself can't be selected (only the whole strip is selected),
    // however, it still has children, and it should serve as a container
    // for the gap.
    const children = editorAPI.components.getChildren(selectedComp);
    if (children.length === 1) {
      return children[0];
    }
  }

  return selectedComp;
}

function getVisibleAndSelectableChildren(
  editorAPI: EditorAPI,
  compRef: CompRef,
  getLayout: (compRef: CompRef) => CompLayout,
) {
  return editorAPI.components
    .getChildren(compRef)
    .filter(editorAPI.components.is.selectable)
    .filter(
      (child: CompRef) =>
        getLayout(child).width > 0 && getLayout(child).height > 0,
    );
}
